import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Trophy, Users, Loader2 } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { SearchAPI } from "@/lib/api";
import { getSport } from "@/lib/sports";

const EMPTY = { players: [], clubs: [], tournaments: [] };

export default function SearchPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setQ("");
      setResults(EMPTY);
      setLoading(false);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const data = await SearchAPI.query(term);
        if (id === reqId.current) setResults(data);
      } catch {
        if (id === reqId.current) setResults(EMPTY);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const total =
    results.players.length + results.clubs.length + results.tournaments.length;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <div data-testid="search-palette" className="bg-[#0A0B0E] text-white">
        <div className="relative">
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Rechercher un joueur, une équipe ou un tournoi…"
            data-testid="search-input"
            className="text-white placeholder:text-zinc-500"
          />
          {loading && (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-500" />
          )}
        </div>

        <CommandList className="max-h-[60vh]">
          {!q.trim() && (
            <div className="px-4 py-10 text-center text-xs uppercase tracking-[0.25em] text-zinc-500 font-body">
              Tape un nom pour lancer la recherche
            </div>
          )}

          {q.trim() && !loading && total === 0 && (
            <CommandEmpty data-testid="search-empty">
              <div className="py-6 text-center text-sm text-zinc-400 font-body">
                Aucun résultat pour «&nbsp;{q}&nbsp;»
              </div>
            </CommandEmpty>
          )}

          {results.players.length > 0 && (
            <CommandGroup
              heading="Joueurs"
              data-testid="search-group-players"
              className="text-zinc-400"
            >
              {results.players.map((p) => {
                const sport = getSport(p.sport || "football");
                const accent = p.primary_color || sport.color;
                return (
                  <CommandItem
                    key={`p-${p.id}`}
                    value={`player-${p.name}-${p.id}`}
                    onSelect={() => go(`/player/${p.slug}`)}
                    data-testid={`search-result-player-${p.slug}`}
                    className="!py-2.5 cursor-pointer aria-selected:bg-white/5 text-white"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Avatar
                        src={p.hero_image_url}
                        fallback={p.name?.[0]}
                        accent={accent}
                        icon={User}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold uppercase tracking-tight text-sm truncate">
                          {p.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 truncate">
                          {[p.position, p.club_name, sport.label]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      {p.jersey_number != null && (
                        <span
                          className="font-display font-black text-base tabular-nums"
                          style={{ color: accent }}
                        >
                          #{p.jersey_number}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {results.clubs.length > 0 && (
            <>
              {results.players.length > 0 && <CommandSeparator />}
              <CommandGroup
                heading="Équipes"
                data-testid="search-group-clubs"
                className="text-zinc-400"
              >
                {results.clubs.map((c) => (
                  <CommandItem
                    key={`c-${c.name}`}
                    value={`club-${c.name}`}
                    onSelect={() =>
                      go(`/?team=${encodeURIComponent(c.name)}#roster`)
                    }
                    data-testid={`search-result-club-${c.name
                      .replace(/\s+/g, "-")
                      .toLowerCase()}`}
                    className="!py-2.5 cursor-pointer aria-selected:bg-white/5 text-white"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Avatar
                        src={c.logo}
                        fallback={c.name?.[0]}
                        accent={c.color}
                        icon={Users}
                        contain
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold uppercase tracking-tight text-sm truncate">
                          {c.name}
                        </div>
                        <div
                          className="text-[10px] uppercase tracking-[0.22em] truncate"
                          style={{ color: c.color }}
                        >
                          {c.count} joueur{c.count > 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {results.tournaments.length > 0 && (
            <>
              {(results.players.length > 0 || results.clubs.length > 0) && (
                <CommandSeparator />
              )}
              <CommandGroup
                heading="Tournois"
                data-testid="search-group-tournaments"
                className="text-zinc-400"
              >
                {results.tournaments.map((t) => {
                  const sport = getSport(t.sport || "football");
                  const accent = t.primary_color || "#D4AF37";
                  return (
                    <CommandItem
                      key={`t-${t.id}`}
                      value={`tournament-${t.name}-${t.id}`}
                      onSelect={() => go(`/tournament/${t.slug}`)}
                      data-testid={`search-result-tournament-${t.slug}`}
                      className="!py-2.5 cursor-pointer aria-selected:bg-white/5 text-white"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <Avatar
                          src={t.cover_image_url}
                          fallback={t.name?.[0]}
                          accent={accent}
                          icon={Trophy}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold uppercase tracking-tight text-sm truncate">
                            {t.name}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 truncate">
                            {[t.location, t.date, sport.label]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>

        <div className="border-t border-white/5 px-3 py-2 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-body">
          <div className="flex items-center gap-1.5">
            <Search className="w-3 h-3" />
            Recherche globale
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <kbd className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-zinc-400">
              ↵
            </kbd>
            <span>ouvrir</span>
            <kbd className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-zinc-400 ml-2">
              esc
            </kbd>
            <span>fermer</span>
          </div>
        </div>
      </div>
    </CommandDialog>
  );
}

const Avatar = ({ src, fallback, accent, icon: Icon, contain = false }) => (
  <div
    className="w-10 h-10 rounded-full shrink-0 overflow-hidden flex items-center justify-center border"
    style={{
      background: `${accent}1F`,
      borderColor: `${accent}55`,
    }}
  >
    {src ? (
      <img
        src={src}
        alt=""
        className={`w-full h-full ${contain ? "object-contain p-1.5" : "object-cover"}`}
      />
    ) : Icon ? (
      <Icon className="w-4 h-4" style={{ color: accent }} />
    ) : (
      <span
        className="font-display font-black text-base"
        style={{ color: accent }}
      >
        {fallback}
      </span>
    )}
  </div>
);
